import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Globe, Target, TrendingUp, Search, MapPin, Users, Zap, BarChart3, Link2, FileText, ArrowRight } from "lucide-react";

const Section = ({ title, children, icon: Icon, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4 bg-[#0d1424]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors text-left">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#4285F4]" />
          <span className="text-white font-bold text-lg">{title}</span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 text-gray-300 text-sm leading-relaxed">{children}</div>}
    </div>
  );
};

export default function SeoPlan() {
  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#4285F4]/10 border border-[#4285F4]/30 text-[#4285F4] text-sm font-bold mb-6">
            <Search className="w-4 h-4" /> PIANO SEO STRATEGICO
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            Progetto <span className="text-[#4285F4]">TANDEM</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-3">
            Due domini, una strategia. Il .com attrae. Il .it converte e vende.
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <span className="text-lg font-black text-[#D4AF37]">
              schedabusiness.com
            </span>
            <Link2 className="w-5 h-5 text-gray-500" />
            <span className="text-lg font-black text-[#D4AF37]">
              schedabusiness.it
            </span>
          </div>
        </motion.div>

        {/* 1. La Strategia TANDEM */}
        <Section title="La Strategia TANDEM — Come Funziona" icon={Zap} defaultOpen={true}>
          <div className="bg-[#4285F4]/10 border border-[#4285F4]/30 rounded-xl p-5 mb-4">
            <h4 className="text-white font-bold text-base mb-2">Il Principio</h4>
            <p className="text-gray-300">
              Avere due domini che puntano allo stesso business non e' duplicazione — e' <strong className="text-white">dominazione della SERP</strong>.
              Google premia i brand con presenza multipla coerente. Con il TANDEM, occupiamo piu' spazio nei risultati di ricerca
              e guidiamo il traffico in modo strategico.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h4 className="text-[#4285F4] font-bold text-base mb-2">.com — Il Magnete</h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>Attrae traffico internazionale e nazionale</li>
                <li>Posiziona per keyword generiche: "assistenza Google Business", "virtual tour 360"</li>
                <li>Brand awareness: il .com e' percepito come autorevole</li>
                <li>Contenuti educativi (Academy, Brand Book)</li>
                <li>hreflang="en" (default internazionale)</li>
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h4 className="text-[#EA4335] font-bold text-base mb-2">.it — Il Closer</h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>Converte il traffico locale toscano</li>
                <li>Posiziona per keyword locali: "virtual tour Siena", "fotografo 360 Toscana"</li>
                <li>Landing page settoriali per sponsorizzate (hotel, cantine, wedding, dentisti)</li>
                <li>Copy PNL aggressivo orientato alla chiusura</li>
                <li>hreflang="it" (mercato italiano)</li>
              </ul>
            </div>
          </div>

          <div className="bg-[#34A853]/10 border border-[#34A853]/30 rounded-xl p-5">
            <h4 className="text-white font-bold text-base mb-2">Il Flusso</h4>
            <div className="flex items-center gap-4 justify-center text-center">
              <div className="text-sm"><span className="text-[#4285F4] font-bold">.com</span><br />attrae</div>
              <ArrowRight className="w-5 h-5 text-gray-500" />
              <div className="text-sm"><span className="text-[#FBBC04] font-bold">cross-link</span><br />collega</div>
              <ArrowRight className="w-5 h-5 text-gray-500" />
              <div className="text-sm"><span className="text-[#EA4335] font-bold">.it</span><br />converte</div>
              <ArrowRight className="w-5 h-5 text-gray-500" />
              <div className="text-sm"><span className="text-[#34A853] font-bold">WhatsApp</span><br />vende</div>
            </div>
          </div>
        </Section>

        {/* 2. Keyword Strategy Toscana */}
        <Section title="Keyword Strategy — Toscana" icon={Search}>
          <h4 className="text-white font-bold mb-3 text-base">Keyword Primarie (.it — Local Intent)</h4>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-white/10 text-left"><th className="py-2 text-gray-400">Keyword</th><th className="py-2 text-gray-400">Volume</th><th className="py-2 text-gray-400">Difficolta'</th><th className="py-2 text-gray-400">Landing</th></tr></thead>
              <tbody>
                {[
                  ["virtual tour 360 siena", "110/mese", "Bassa", "/hotel"],
                  ["fotografo google street view toscana", "90/mese", "Bassa", "/"],
                  ["virtual tour ristorante toscana", "70/mese", "Bassa", "/hotel"],
                  ["virtual tour cantina chianti", "50/mese", "Bassa", "/cantine"],
                  ["tour virtuale agenzia immobiliare", "140/mese", "Media", "/agenzie"],
                  ["location matrimoni toscana virtual tour", "60/mese", "Bassa", "/wedding"],
                  ["foto 360 gradi per attivita", "90/mese", "Bassa", "/dentisti"],
                  ["google business profile siena", "40/mese", "Bassa", "/"],
                  ["formazione digitale scuole toscana", "30/mese", "Bassa", "/academy"],
                  ["pubblicita online per aziende siena", "50/mese", "Media", "/"],
                ].map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 text-white font-medium">{r[0]}</td>
                    <td className="py-2 text-gray-400">{r[1]}</td>
                    <td className="py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r[2] === 'Bassa' ? 'bg-[#34A853]/20 text-[#34A853]' : 'bg-[#FBBC04]/20 text-[#FBBC04]'}`}>{r[2]}</span></td>
                    <td className="py-2 text-[#4285F4] font-mono">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="text-white font-bold mb-3 text-base">Keyword Supporto (.com — Broad Intent)</h4>
          <div className="grid grid-cols-2 gap-2">
            {["scheda google sospesa", "come recuperare scheda google", "assistenza business profile", "virtual tour google maps", "gopro max virtual tour", "come fare tour virtuale", "costo virtual tour 360", "fotografo certificato google"].map((kw, i) => (
              <div key={i} className="bg-white/5 rounded-lg px-3 py-2 text-xs text-gray-400">{kw}</div>
            ))}
          </div>
        </Section>

        {/* 3. Architettura SEO */}
        <Section title="Architettura SEO dei Due Siti" icon={Globe}>
          <h4 className="text-white font-bold mb-3 text-base">Hreflang (Cross-Reference)</h4>
          <pre className="bg-black/40 border border-white/10 rounded-lg p-4 text-xs text-green-400 font-mono mb-4">{`<!-- Su schedabusiness.it -->
<link rel="alternate" hreflang="it" href="https://schedabusiness.it" />
<link rel="alternate" hreflang="en" href="https://schedabusiness.com" />
<link rel="alternate" hreflang="x-default" href="https://schedabusiness.com" />

<!-- Su schedabusiness.com -->
<link rel="alternate" hreflang="en" href="https://schedabusiness.com" />
<link rel="alternate" hreflang="it" href="https://schedabusiness.it" />
<link rel="alternate" hreflang="x-default" href="https://schedabusiness.com" />`}</pre>

          <h4 className="text-white font-bold mb-3 text-base">Canonical Strategy</h4>
          <p className="text-gray-300 mb-4">
            Ogni pagina ha il proprio canonical. <strong className="text-white">Non si duplicano contenuti tra .it e .com</strong> — si completano.
            Il .com ha contenuti educativi (Academy, brand awareness). Il .it ha contenuti commerciali (landing settoriali, prezzi, CTA).
          </p>

          <h4 className="text-white font-bold mb-3 text-base">Internal Linking</h4>
          <div className="space-y-2">
            {[
              { from: ".com (Homepage)", to: ".it (Homepage)", why: "Footer cross-link + hreflang" },
              { from: ".com (Brand Book)", to: ".it (Brand Book)", why: "Versione locale della documentazione" },
              { from: ".com (Academy)", to: ".it (Academy)", why: "Stessa missione, adattata al territorio" },
              { from: ".it (Landing Hotel)", to: ".com (Virtual Tour)", why: "Approfondimento tecnico su .com" },
              { from: ".it (Pacchetti)", to: ".com (Pacchetti)", why: "Confronto prezzi e dettagli completi" },
            ].map((l, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2 text-xs">
                <span className="text-[#4285F4] font-medium w-36">{l.from}</span>
                <ArrowRight className="w-3 h-3 text-gray-500" />
                <span className="text-[#EA4335] font-medium w-36">{l.to}</span>
                <span className="text-gray-500 flex-1">{l.why}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* 4. Comparti di Mercato */}
        <Section title="Comparti da Conquistare in Toscana" icon={Target}>
          <h4 className="text-white font-bold mb-3 text-base">Comparto 1: Formazione Digitale</h4>
          <div className="bg-[#4285F4]/10 border border-[#4285F4]/30 rounded-xl p-5 mb-4">
            <p className="text-gray-300 mb-3">
              La formazione digitale in Toscana e' un mercato vergine. Pochissimi operatori offrono formazione su Google Business Profile,
              fotografia 360° e marketing territoriale. Con il programma <strong className="text-white">S.it Cares (#its4kids)</strong> entriamo nelle scuole
              e creiamo brand awareness a costo zero.
            </p>
            <ul className="space-y-1 text-sm">
              <li className="text-gray-300"><strong className="text-white">Target:</strong> Dirigenti scolastici, assessori alla cultura, Pro Loco, Confcommercio</li>
              <li className="text-gray-300"><strong className="text-white">Canale:</strong> Email outreach + social + eventi locali</li>
              <li className="text-gray-300"><strong className="text-white">Keyword:</strong> "formazione digitale scuole toscana", "corso google business siena"</li>
              <li className="text-gray-300"><strong className="text-white">Landing:</strong> /academy + /enti</li>
              <li className="text-gray-300"><strong className="text-white">Conversione:</strong> La scuola diventa referral → i genitori sono imprenditori locali → diventano clienti</li>
            </ul>
          </div>

          <h4 className="text-white font-bold mb-3 text-base">Comparto 2: Pubblicita' Online per PMI</h4>
          <div className="bg-[#EA4335]/10 border border-[#EA4335]/30 rounded-xl p-5 mb-4">
            <p className="text-gray-300 mb-3">
              Le PMI toscane (hotel, ristoranti, cantine, studi) spendono in media <strong className="text-white">€2.000-5.000/anno</strong> in pubblicita' tradizionale
              (riviste, cartelloni, sagre). Il Virtual Tour e' <strong className="text-white">pubblicita' permanente a €99</strong> che resta online per sempre su Google Maps.
              Il nostro lavoro e' spostare quel budget dal cartaceo al digitale.
            </p>
            <ul className="space-y-1 text-sm">
              <li className="text-gray-300"><strong className="text-white">Target:</strong> Titolari 45-65 anni, zero competenze digitali, decidono e pagano in prima persona</li>
              <li className="text-gray-300"><strong className="text-white">Canale:</strong> Facebook/Instagram Ads (70%) + Google Ads (30%)</li>
              <li className="text-gray-300"><strong className="text-white">Keyword:</strong> "pubblicita ristorante siena", "come farsi trovare su google maps"</li>
              <li className="text-gray-300"><strong className="text-white">Landing:</strong> /hotel, /cantine, /wedding, /dentisti (una per settore)</li>
              <li className="text-gray-300"><strong className="text-white">Conversione:</strong> Landing settoriale → WhatsApp → Demo → Chiusura</li>
            </ul>
          </div>

          <h4 className="text-white font-bold mb-3 text-base">Il Vantaggio Competitivo</h4>
          <div className="bg-[#34A853]/10 border border-[#34A853]/30 rounded-xl p-5">
            <ul className="space-y-2 text-sm text-gray-300">
              <li><strong className="text-[#34A853]">Monopolio locale:</strong> Nel 2026 in Toscana nessuno offre Virtual Tour 360° a €99 con pubblicazione Google Maps. Siamo soli.</li>
              <li><strong className="text-[#34A853]">Doppia SERP:</strong> Con .com e .it occupiamo 2 risultati su 10 per ogni keyword. Il concorrente ne ha 1 al massimo.</li>
              <li><strong className="text-[#34A853]">Deducibilita':</strong> Il 100% deducibile e' l'argomento killer per il commercialista del cliente. Nessun competitor lo comunica cosi' forte.</li>
              <li><strong className="text-[#34A853]">WhatsApp funnel:</strong> Zero form, zero email. WhatsApp → risposta in 5 minuti → chiusura. Il percorso piu' corto possibile.</li>
              <li><strong className="text-[#34A853]">Academy = Trust:</strong> Chi insegna nelle scuole e' percepito come autorita'. Non vendi — educhi. E chi educa, vende di piu'.</li>
            </ul>
          </div>
        </Section>

        {/* 5. Piano Operativo */}
        <Section title="Piano Operativo — 90 Giorni" icon={BarChart3}>
          <div className="space-y-4">
            {[
              {
                phase: "Fase 1 — Settimana 1-2",
                color: "#4285F4",
                title: "Setup e Lancio",
                tasks: [
                  "Deploy .it su schedabusiness.it (in attesa fix Emergent)",
                  "Attivare Google Search Console per entrambi i domini",
                  "Verificare hreflang, sitemap, robots.txt su entrambi",
                  "Creare Google Ads account con keyword locali",
                  "Preparare 3 creativita' Facebook per ogni settore (hotel, cantine, wedding)",
                ]
              },
              {
                phase: "Fase 2 — Settimana 3-6",
                color: "#FBBC04",
                title: "Acquisizione — Pubblicita' Online",
                tasks: [
                  "Lanciare Facebook Ads: €10/giorno per settore (4 settori = €40/giorno)",
                  "Lanciare Google Ads: €15/giorno su keyword locali a bassa concorrenza",
                  "A/B test landing page (headline, CTA, immagini)",
                  "Monitorare WhatsApp conversion rate (target: 5% dei click)",
                  "Email outreach: 20 agenzie/ristoranti/cantine a settimana",
                ]
              },
              {
                phase: "Fase 3 — Settimana 7-10",
                color: "#34A853",
                title: "Formazione — Scuole e Enti",
                tasks: [
                  "Contattare 10 dirigenti scolastici zona Siena/Valdelsa",
                  "Proporre programma S.it Cares (#its4kids) via email personalizzata",
                  "Organizzare 1 evento pilota in una scuola elementare",
                  "Documentare con foto/video per social proof",
                  "Cross-post su .com e .it per SEO e brand awareness",
                ]
              },
              {
                phase: "Fase 4 — Settimana 11-12",
                color: "#EA4335",
                title: "Ottimizzazione e Scala",
                tasks: [
                  "Analisi ROI: costo per lead WhatsApp vs fatturato generato",
                  "Tagliare settori con CPA > €30, raddoppiare budget su quelli < €15",
                  "Aggiungere testimonianze clienti reali sul .it",
                  "Creare caso studio scuola per PR locale (giornali, TV)",
                  "Pianificare espansione verso Firenze/Arezzo (raggio 80km da Siena)",
                ]
              },
            ].map((phase, i) => (
              <div key={i} className="rounded-xl p-5" style={{ backgroundColor: `${phase.color}10`, border: `1px solid ${phase.color}30` }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: phase.color }} />
                  <span className="text-xs font-bold" style={{ color: phase.color }}>{phase.phase}</span>
                </div>
                <h4 className="text-white font-bold text-base mb-3">{phase.title}</h4>
                <ul className="space-y-1">
                  {phase.tasks.map((t, j) => (
                    <li key={j} className="text-gray-300 text-sm flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: phase.color }} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* 6. Metriche */}
        <Section title="KPI e Metriche di Successo" icon={TrendingUp}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left"><th className="py-2 text-gray-400">Metrica</th><th className="py-2 text-gray-400">Target 90gg</th><th className="py-2 text-gray-400">Strumento</th></tr></thead>
              <tbody>
                {[
                  ["Posizionamento .it top 10 keyword locali", "6/10 keyword", "Google Search Console"],
                  ["Posizionamento .com top 10 keyword broad", "4/8 keyword", "Google Search Console"],
                  ["Click WhatsApp da landing settoriali", "150/mese", "UTM + WhatsApp Business"],
                  ["Conversion rate WhatsApp → Cliente", "15%", "CRM manuale"],
                  ["Costo per lead (CPL) Facebook Ads", "< €12", "Meta Ads Manager"],
                  ["Costo per lead (CPL) Google Ads", "< €8", "Google Ads"],
                  ["Scuole contattate", "10", "Email tracker"],
                  ["Scuole attivate con S.it Cares", "2-3", "Calendario"],
                  ["Fatturato generato (90gg)", "€3.000-5.000", "Fatture"],
                ].map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 text-white">{r[0]}</td>
                    <td className="py-2 text-[#34A853] font-bold">{r[1]}</td>
                    <td className="py-2 text-gray-400">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 7. Budget */}
        <Section title="Budget Stimato — 90 Giorni" icon={FileText}>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left"><th className="py-2 text-gray-400">Voce</th><th className="py-2 text-gray-400">Costo/mese</th><th className="py-2 text-gray-400">Totale 90gg</th></tr></thead>
              <tbody>
                {[
                  ["Facebook/Instagram Ads (4 settori)", "€1.200", "€3.600"],
                  ["Google Ads (keyword locali)", "€450", "€1.350"],
                  ["Hosting/Domini (.it + .com)", "€10", "€30"],
                  ["Strumenti SEO (Search Console = gratis)", "€0", "€0"],
                  ["Materiale S.it Cares (stampe, gadget)", "€50", "€150"],
                ].map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 text-white">{r[0]}</td>
                    <td className="py-2 text-gray-400">{r[1]}</td>
                    <td className="py-2 text-white font-bold">{r[2]}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#34A853]/30">
                  <td className="py-3 text-[#34A853] font-bold">TOTALE</td>
                  <td className="py-3 text-gray-400">€1.710/mese</td>
                  <td className="py-3 text-[#34A853] font-bold text-lg">€5.130</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-400 text-xs">ROI atteso: con 10 clienti chiusi a €299 medio = €2.990/mese. Break-even al mese 2. Dal mese 3 in poi: profitto netto.</p>
        </Section>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-white/10 text-center">
          <p className="text-gray-500 text-xs">Piano SEO Strategico — Progetto TANDEM</p>
          <p className="text-gray-600 text-[10px] mt-1">schedabusiness.it + schedabusiness.com — Riccardo Rossi, 2026</p>
        </div>
      </div>
    </div>
  );
}
